import { useEffect, useState } from "preact/hooks";

import { Modal } from "./Modal";
import { StatsGrid } from "./StatsGrid";
import { useComputed } from "@preact/signals";

import {
  isScanning,
  scanErrors,
  scanHistory,
  scanStatus,
  showToast,
} from "../store/appStore";
import { fetchSkippedCompanies, fetchUnresolvedCompanies, fixScanError } from "../lib/api";
import type { SkippedCompany, UnresolvedCompany } from "../lib/types";
import { refreshScanErrors } from "../hooks/useJobs";

interface Props {
  open: boolean;
  onClose: () => void;
}

/** Diagnostics drawer — combines the scan console, scan history, scan
 * errors, and the unresolved-company queue for manual ATS work. */
export function DiagnosticsDrawer({ open, onClose }: Props) {
  const [fixingCompany, setFixingCompany] = useState<string | null>(null);
  const [unresolvedCompanies, setUnresolvedCompanies] = useState<UnresolvedCompany[]>([]);
  const [skippedCompanies, setSkippedCompanies] = useState<SkippedCompany[]>([]);
  const logs = useComputed(() => scanStatus.value?.logs ?? []);
  const progress = useComputed(() => scanStatus.value?.progress ?? null);
  const errorCount = useComputed(() => scanErrors.value.length);

  useEffect(() => {
    if (!open) return;
    refreshScanErrors();
    fetchUnresolvedCompanies()
      .then(setUnresolvedCompanies)
      .catch((err) => console.error("Failed to load unresolved companies", err));
    fetchSkippedCompanies()
      .then(setSkippedCompanies)
      .catch((err) => console.error("Failed to load skipped companies", err));
  }, [open]);

  async function handleFix(companyName: string) {
    setFixingCompany(companyName);
    try {
      const res = await fixScanError(companyName);
      showToast(res.message);
      refreshScanErrors();
      const next = await fetchUnresolvedCompanies();
      setUnresolvedCompanies(next);
    } catch (err) {
      console.error(err);
      showToast(err instanceof Error ? err.message : "AI Fix failed");
    } finally {
      setFixingCompany(null);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Diagnostics" maxWidth="780px">
      <div class="diagnostics">
        <section class="diag-section">
          <h3>Overview</h3>
          <StatsGrid compact />
        </section>

        {/* Scan progress + console */}
        <section class="diag-section">
          <div class="diag-section-header">
            <h3>Console</h3>
            {isScanning.value && <span class="diag-badge diag-badge-running">Running</span>}
          </div>
          {progress.value && progress.value.total > 0 && (
            <div class="diag-progress">
              <div class="diag-progress-bar" style={{ width: `${(progress.value.current / progress.value.total) * 100}%` }} />
              <span class="diag-progress-text">
                {progress.value.current} / {progress.value.total} companies
              </span>
            </div>
          )}
          <div class="diag-console" role="log" aria-live="polite">
            {logs.value.length === 0 && <div class="diag-console-empty">No scan output yet.</div>}
            {logs.value.map((line, idx) => (
              <div
                key={idx}
                class={`diag-console-line${line.includes("✨ NEW MATCH:") ? " new-match" : line.toLowerCase().includes("error") ? " error" : ""}`}
              >
                {line}
              </div>
            ))}
          </div>
        </section>

        {/* Unresolved companies from add-company */}
        <section class="diag-section">
          <div class="diag-section-header">
            <h3>Needs ATS Support</h3>
            {unresolvedCompanies.length > 0 && (
              <span class="diag-badge diag-badge-warning">{unresolvedCompanies.length}</span>
            )}
          </div>
          {unresolvedCompanies.length === 0 ? (
            <div class="diag-empty">No companies waiting for ATS support.</div>
          ) : (
            <>
              <p class="form-hint">
                These companies were saved as <code>unsupported</code> because auto-detect failed.
                Retry resolve here, edit them in Settings → Companies, or use an AI agent against
                this list when adding parser support.
              </p>
              <ul class="diag-errors-list">
                {unresolvedCompanies.map((company) => (
                  <li key={`${company.slug}-${company.name}`} class="diag-error-card diag-pending-card">
                    <div class="diag-error-header">
                      <span class="diag-error-title">{company.name}</span>
                      <button
                        class="diag-fix-btn"
                        onClick={() => handleFix(company.name)}
                        disabled={fixingCompany !== null}
                      >
                        {fixingCompany === company.name ? "Retrying…" : "Retry resolve"}
                      </button>
                    </div>
                    <div class="diag-error-msg">
                      Query: <code>{company.original_query}</code>
                      {company.unresolved_at ? ` · saved ${company.unresolved_at} UTC` : ""}
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>

        {/* Permanently skipped companies */}
        <section class="diag-section">
          <div class="diag-section-header">
            <h3>Skipped Entirely</h3>
            {skippedCompanies.length > 0 && (
              <span class="diag-badge">{skippedCompanies.length}</span>
            )}
          </div>
          {skippedCompanies.length === 0 ? (
            <div class="diag-empty">No companies are permanently skipped.</div>
          ) : (
            <>
              <p class="form-hint">
                These companies are intentionally excluded from scans because they have no public ATS
                feed or were marked unsupported manually.
              </p>
              <ul class="diag-errors-list">
                {skippedCompanies.map((company) => (
                  <li key={`${company.slug}-${company.name}`} class="diag-error-card diag-skipped-card">
                    <div class="diag-error-header">
                      <span class="diag-error-title">{company.name}</span>
                      <span class="diag-skipped-label">{company.skip_reason_label}</span>
                    </div>
                    <div class="diag-error-msg">
                      Slug: <code>{company.slug}</code>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>

        {/* Scan history */}
        <section class="diag-section">
          <h3>Scan History</h3>
          {scanHistory.value.length === 0 ? (
            <div class="diag-empty">No scans recorded yet.</div>
          ) : (
            <ul class="diag-history-list">
              {scanHistory.value.map((run) => {
                const d = new Date(run.run_time.replace(" ", "T") + "Z");
                const dateStr = isNaN(d.getTime()) ? run.run_time : d.toLocaleString();
                return (
                  <li key={run.id} class="diag-history-item">
                    <span>Run #{run.id} — {dateStr}</span>
                    <span class="diag-history-count">{run.count} jobs</span>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* Scan errors with AI Fix */}
        <section class="diag-section">
          <div class="diag-section-header">
            <h3>Scan Issues</h3>
            {errorCount.value > 0 && <span class="diag-badge diag-badge-error">{errorCount.value}</span>}
          </div>
          {scanErrors.value.length === 0 ? (
            <div class="diag-empty">No active scan issues.</div>
          ) : (
            <ul class="diag-errors-list">
              {scanErrors.value.map((err) => (
                <li key={err.id} class="diag-error-card">
                  <div class="diag-error-header">
                    <span class="diag-error-title">{err.company_name} ({err.ats || "unknown"})</span>
                    <button
                      class="diag-fix-btn"
                      onClick={() => handleFix(err.company_name)}
                      disabled={fixingCompany !== null}
                    >
                      {fixingCompany === err.company_name ? "Fixing…" : "⚡ AI Fix"}
                    </button>
                  </div>
                  <div class="diag-error-msg">{err.error_message}</div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </Modal>
  );
}
